import React, { useState, useEffect } from 'react';
import { Search, Filter, X, Plus, Eye, LayoutGrid, List, AlertCircle, LogOut } from 'lucide-react';
import TenantCardView from './TenantCardView';
import TenantForm from './TenantForm';
import { getAllTenants, getTenantTransactions } from './api';
import { useAppTheme } from '../../contexts/ThemeContext';

const TenantsManagement = () => {
  const { palette, Colors } = useAppTheme();

  const [tenants, setTenants]               = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [viewMode, setViewMode]             = useState('table');
  const [searchTerm, setSearchTerm]         = useState('');
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState(null);
  const [showAddTenantForm, setShowAddTenantForm] = useState(false);
  const [showInactive, setShowInactive]     = useState(false);
  const [filters, setFilters]               = useState({ apartment: '', category: '', status: '', balanceStatus: '', sortBy: '' });

  useEffect(() => { fetchTenants(); }, [showInactive]);
  

  const fetchTenants = async () => {
    setLoading(true); setError(null);
    try { setTenants(await getAllTenants({ includeInactive: showInactive })); }
    catch { setError('Failed to load tenants. Please try again.'); }
    finally { setLoading(false); }
  };

  // Refreshes list AND updates the open card in-place after any save
  const handleTenantUpdate = async () => {
    setError(null);
    try {
      const fresh = await getAllTenants({ includeInactive: showInactive });
      setTenants(fresh);
      if (selectedTenant) {
        const updated = fresh.find(t => t.id === selectedTenant.id);
        if (updated) {
          const formatted = formatTenantForDisplay(updated);
          try {
            const txns = await getTenantTransactions(updated.id);
            formatted.transactions = txns.map(txn => ({
              type:        txn.amount > 0 ? 'payment' : 'charge',
              description: txn.description || txn.type,
              date:        new Date(txn.date).toLocaleDateString(),
              amount:      txn.amount,
              method:      txn.paymentMethod || 'System',
            }));
          } catch { formatted.transactions = selectedTenant.transactions || []; }
          setSelectedTenant(formatted);
        }
      }
    } catch { setError('Failed to refresh tenants.'); }
  };

  // ── status helpers ────────────────────────────────────────────────────────
  const calculateStatus = (t) => {
    if (t.status === 'Moved Out') return 'Moved Out';
    if (t.is_active === false)    return 'Moved Out';
    if (t.balance < 0) return 'Past Due';
    if (t.status === 'Active' && t.leaseEnd) {
      const days = Math.ceil((new Date(t.leaseEnd) - new Date()) / 86400000);
      if (days <= 30 && days >= 0) return 'Ending Soon';
    }
    return t.status || 'Active';
  };

  const getStatusStyle = (status) => {
    if (status === 'Active')      return { backgroundColor: Colors.status.success, color: '#fff' };
    if (status === 'Past Due')    return { backgroundColor: Colors.status.error,   color: '#fff' };
    if (status === 'Ending Soon') return { backgroundColor: Colors.accent.blue,    color: '#fff' };
    if (status === 'Moved Out')   return { backgroundColor: '#6B7280',             color: '#fff' };
    return { backgroundColor: palette.text.muted, color: '#fff' };
  };

  // ── format ────────────────────────────────────────────────────────────────
  const formatTenantForDisplay = (t) => ({
    id:          t.id,
    name:        `${t.firstName} ${t.lastName}`,
    initials:    `${t.firstName?.charAt(0)}${t.lastName?.charAt(0)}`,
    email:       t.email,
    phone:       t.phone,
    idNumber:    t.idNumber,
    accountId:   t.account_id,
    property:    t.propertyName,
    propertyName: t.propertyName,
    unit:        t.houseNumber,
    houseNumber: t.houseNumber,
    category:    t.category,
    rent:        t.rent || t.initialRent || 0,
    balance:     t.balance || 0,
    status:      calculateStatus(t),
    isActive:    t.is_active !== false,
    movedOutAt:  t.moved_out_at || null,
    leaseStart:  t.leaseStart ? new Date(t.leaseStart).toLocaleDateString() : 'N/A',
    leaseEnd:    t.leaseEnd   ? new Date(t.leaseEnd).toLocaleDateString()   : 'N/A',
    securityDeposit: t.deposit    || 0,
    waterBill:       t.waterBill  || 0,
    transactions:    t.transactions || [],
    additionalPhones: t.additionalPhones || [],
  });

  // ── select / add ──────────────────────────────────────────────────────────
  const handleSelectTenant = async (tenant) => {
    const formatted = formatTenantForDisplay(tenant);
    try {
      const txns = await getTenantTransactions(tenant.id);
      formatted.transactions = txns.map(txn => ({
        type:        txn.amount > 0 ? 'payment' : 'charge',
        description: txn.description || txn.type,
        date:        new Date(txn.date).toLocaleDateString(),
        amount:      txn.amount,
        method:      txn.paymentMethod || 'System',
      }));
    } catch { formatted.transactions = []; }
    setSelectedTenant(formatted);
  };

  const handleTenantAdded = () => { fetchTenants(); setShowAddTenantForm(false); };

  // ── last name extractor ───────────────────────────────────────────────────
  // firstName is always the first word only; lastName may be multiple words
  // e.g. firstName="Obadiah" lastName="Gitau Muritu" → last part = "Muritu"
  const getLastName = (t) => {
    const parts = `${t.firstName ?? ''} ${t.lastName ?? ''}`.trim().split(/\s+/);
    return parts[parts.length - 1].toLowerCase();
  };

  // ── filter / search ───────────────────────────────────────────────────────
  const apartments = [...new Set(tenants.map(t => t.propertyName).filter(Boolean))];
  const categories = [...new Set(tenants.map(t => t.category).filter(Boolean))];

  const filteredTenants = tenants
    .filter(t => {
      const name = `${t.firstName} ${t.lastName}`.toLowerCase();
      if (searchTerm &&
        !name.includes(searchTerm.toLowerCase()) &&
        !t.email?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !t.phone?.includes(searchTerm) &&
        !t.idNumber?.includes(searchTerm)) return false;
      if (filters.apartment    && t.propertyName     !== filters.apartment)    return false;
      if (filters.category     && t.category         !== filters.category)     return false;
      if (filters.status       && calculateStatus(t) !== filters.status)       return false;
      if (filters.balanceStatus === 'With Arrears' && t.balance >= 0)  return false;
      if (filters.balanceStatus === 'No Balance'   && t.balance !== 0) return false;
      if (filters.balanceStatus === 'With Balance' && t.balance <= 0)  return false;
      return true;
    })
    .sort((a, b) => {
      if (filters.sortBy === 'lastName') {
        return getLastName(a).localeCompare(getLastName(b));
      }
      return 0; // default: preserve backend order
    });
    

  const activeFilterCount = Object.values(filters).filter(Boolean).length + (searchTerm ? 1 : 0);

  // ── style tokens ──────────────────────────────────────────────────────────
  const bg  = palette.background;
  const txt = palette.text;
  const bdr = palette.border;
  const P   = Colors.primary.main;

  const card  = { backgroundColor: bg.secondary, border: `1px solid ${bdr}`, borderRadius: '12px' };
  const input = { backgroundColor: bg.tertiary,  border: `1px solid ${bdr}`, color: txt.primary, outline: 'none' };

  // ── filter chip display labels ────────────────────────────────────────────
  const filterDisplayLabels = { sortBy: { lastName: 'Last Name' } };
  const getFilterChipLabel = (key, val) => filterDisplayLabels[key]?.[val] ?? val;

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen" style={{ backgroundColor: bg.primary }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="px-6 pt-6 pb-4">
        <div className="rounded-xl p-6" style={card}>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold" style={{ color: P }}>Tenants Management</h1>
              <p className="mt-1" style={{ color: txt.secondary }}>Manage and monitor all your tenants</p>
            </div>

            {!selectedTenant && (
              <div className="flex items-center gap-3">

                {/* View mode toggle */}
                <div className="flex items-center rounded-lg border" style={{ borderColor: bdr }}>
                  {[['table', <List className="w-4 h-4" />], ['card', <LayoutGrid className="w-4 h-4" />]].map(([mode, icon]) => (
                    <button key={mode} onClick={() => setViewMode(mode)}
                      className="px-4 py-2 flex items-center gap-2 font-medium transition-colors capitalize"
                      style={{
                        borderRadius: mode === 'table' ? '6px 0 0 6px' : '0 6px 6px 0',
                        backgroundColor: viewMode === mode ? P : 'transparent',
                        color: viewMode === mode ? '#fff' : txt.secondary,
                      }}>
                      {icon} {mode === 'table' ? 'Table' : 'Cards'}
                    </button>
                  ))}
                </div>

                {/* Show / hide moved-out toggle */}
                <button
                  onClick={() => setShowInactive(s => !s)}
                  className="px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2 transition-all"
                  style={{
                    backgroundColor: showInactive ? 'rgba(107,114,128,0.15)' : 'transparent',
                    border: `1px solid ${showInactive ? '#6B7280' : bdr}`,
                    color: showInactive ? '#6B7280' : txt.secondary,
                  }}>
                  <LogOut className="w-4 h-4" />
                  {showInactive ? 'Hiding Moved Out' : 'Show Moved Out'}
                </button>

                {/* Add tenant */}
                <button onClick={() => setShowAddTenantForm(true)}
                  className="text-white font-semibold px-6 py-2.5 rounded-lg flex items-center gap-2 transition-all"
                  style={{ backgroundColor: P }}>
                  <Plus className="w-5 h-5" /> Add Tenant
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Loading ─────────────────────────────────────────────────────────── */}
      {loading && (
        <div className="px-6 pb-6">
          <div className="rounded-xl p-12 text-center" style={card}>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: P }} />
            <p className="mt-4" style={{ color: txt.secondary }}>Loading tenants...</p>
          </div>
        </div>
      )}

      {/* ── Error ───────────────────────────────────────────────────────────── */}
      {error && (
        <div className="px-6 pb-6">
          <div className="rounded-xl p-4 flex items-start gap-3"
            style={{ backgroundColor: `${Colors.status.error}10`, border: `1px solid ${Colors.status.error}40` }}>
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: Colors.status.error }} />
            <div>
              <p className="font-medium" style={{ color: Colors.status.error }}>{error}</p>
              <button onClick={fetchTenants} className="mt-2 text-sm font-medium underline"
                style={{ color: Colors.status.error }}>Try Again</button>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && (
        <>
          {/* ── Tenant Card View (when a tenant is selected) ─────────────── */}
          {selectedTenant ? (
            <div className="px-6 pb-6">
              <TenantCardView
                tenant={selectedTenant}
                onClose={() => setSelectedTenant(null)}
                onUpdate={handleTenantUpdate}
              />
            </div>
          ) : (
            <>
              {/* ── Search & Filters ─────────────────────────────────────── */}
              <div className="px-6 pb-4">
                <div className="rounded-xl p-6" style={card}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Filter className="w-5 h-5" style={{ color: P }} />
                      <h2 className="text-lg font-semibold" style={{ color: txt.primary }}>Search &amp; Filters</h2>
                    </div>
                    {activeFilterCount > 0 && (
                      <button
                        onClick={() => { setFilters({ apartment: '', category: '', status: '', balanceStatus: '', sortBy: '' }); setSearchTerm(''); }}
                        className="text-sm flex items-center gap-1 px-3 py-1.5 rounded-lg transition-colors"
                        style={{ color: txt.secondary, backgroundColor: bg.tertiary }}>
                        <X className="w-4 h-4" /> Clear All ({activeFilterCount})
                      </button>
                    )}
                  </div>

                  {/* Search bar */}
                  <div className="mb-4">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="w-5 h-5" style={{ color: txt.muted }} />
                      </div>
                      <input type="text"
                        placeholder="Search by name, email, phone, or ID number..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 rounded-lg transition-all"
                        style={input}
                      />
                    </div>
                  </div>

                  {/* Filter dropdowns */}
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    {[
                      ['Apartment', 'apartment',    apartments,                                          apt => apt],
                      ['Category',  'category',     categories,                                          cat => cat],
                      ['Status',    'status',       ['Active', 'Past Due', 'Ending Soon', 'Moved Out'],  s => s],
                      ['Balance',   'balanceStatus',['With Arrears', 'No Balance', 'With Balance'],      b => b],
                      ['Sort By',   'sortBy',       ['Last Name'],                                       () => 'lastName'],
                    ].map(([label, key, opts, valFn]) => (
                      <div key={key}>
                        <label className="block text-sm font-medium mb-2" style={{ color: txt.secondary }}>{label}</label>
                        <select value={filters[key]}
                          onChange={e => setFilters({ ...filters, [key]: e.target.value })}
                          className="w-full px-4 py-2.5 rounded-lg" style={input}>
                          <option value="">{key === 'sortBy' ? 'Default Order' : `All ${label}s`}</option>
                          {opts.map(o => <option key={o} value={valFn(o)}>{o}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>

                  {/* Active filter chips */}
                  {activeFilterCount > 0 && (
                    <div className="flex items-center gap-2 flex-wrap mt-4 pt-4" style={{ borderTop: `1px solid ${bdr}` }}>
                      <span className="text-sm font-medium" style={{ color: txt.secondary }}>Active Filters:</span>
                      {searchTerm && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 text-sm rounded-full font-medium"
                          style={{ backgroundColor: `${P}15`, color: P }}>
                          Search: "{searchTerm}"
                          <button onClick={() => setSearchTerm('')} className="rounded-full p-0.5"><X className="w-3 h-3" /></button>
                        </span>
                      )}
                      {Object.entries(filters).map(([key, val]) => val && (
                        <span key={key} className="inline-flex items-center gap-1 px-3 py-1 text-sm rounded-full font-medium"
                          style={{ backgroundColor: `${P}15`, color: P }}>
                          {key === 'sortBy' ? 'Sort' : key}: {getFilterChipLabel(key, val)}
                          <button onClick={() => setFilters({ ...filters, [key]: '' })} className="rounded-full p-0.5"><X className="w-3 h-3" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Count */}
              <div className="px-6 pb-4">
                <p className="text-sm font-medium" style={{ color: txt.secondary }}>
                  Showing{' '}
                  <span className="font-bold" style={{ color: P }}>{filteredTenants.length}</span>
                  {' '}of{' '}
                  <span className="font-bold">{tenants.length}</span> tenants
                  {filters.sortBy === 'lastName' && (
                    <span className="ml-2 text-xs" style={{ color: txt.muted }}>· sorted by last name (A–Z)</span>
                  )}
                </p>
              </div>

              {/* ── Content: Cards or Table ──────────────────────────────── */}
              <div className="px-6 pb-6">

                {viewMode === 'card' ? (
                  /* ── Card grid ── */
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredTenants.map(tenant => {
                      const f = formatTenantForDisplay(tenant);
                      const isMovedOut = f.status === 'Moved Out';
                      return (
                        <div key={tenant.id} onClick={() => handleSelectTenant(tenant)}
                          className="rounded-xl overflow-hidden cursor-pointer transition-all duration-200 hover:-translate-y-1"
                          style={{ ...card, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', opacity: isMovedOut ? 0.75 : 1 }}>

                          <div className="p-4 text-white"
                            style={{ background: isMovedOut
                              ? 'linear-gradient(135deg, #4B5563, #6B7280)'
                              : `linear-gradient(135deg, ${Colors.primary.dark}, ${Colors.primary.main})` }}>
                            <div className="flex items-center gap-3 mb-2">
                              <div className="w-12 h-12 rounded-full flex items-center justify-center text-base font-bold border-2"
                                style={{ backgroundColor: isMovedOut ? '#9CA3AF' : f.balance < 0 ? Colors.status.error : Colors.status.success, borderColor: 'rgba(255,255,255,0.3)' }}>
                                {f.initials}
                              </div>
                              <div className="flex-1">
                                <h3 className="text-lg font-bold">{f.name}</h3>
                                <p className="text-xs truncate" style={{ color: 'rgba(255,255,255,0.7)' }}>{f.email}</p>
                              </div>
                            </div>
                            <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full"
                              style={getStatusStyle(f.status)}>{f.status}</span>
                          </div>

                          <div className="p-4">
                            <div className="space-y-2 text-sm">
                              {[['Property', f.property], ['Unit', `${f.unit} • ${f.category}`]].map(([l, v]) => (
                                <div key={l} className="flex justify-between">
                                  <span style={{ color: txt.secondary }}>{l}</span>
                                  <span className="font-semibold" style={{ color: txt.primary }}>{v}</span>
                                </div>
                              ))}
                              <div className="flex justify-between pt-2" style={{ borderTop: `1px solid ${bdr}` }}>
                                <span style={{ color: txt.secondary }}>Monthly Rent</span>
                                <span className="font-bold" style={{ color: P }}>KES {f.rent.toLocaleString()}</span>
                              </div>
                              <div className="flex justify-between">
                                <span style={{ color: txt.secondary }}>Balance</span>
                                <span className="font-bold" style={{ color: f.balance < 0 ? Colors.status.error : f.balance === 0 ? txt.secondary : Colors.status.success }}>
                                  KES {Number(f.balance).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="px-4 py-2" style={{ backgroundColor: bg.tertiary, borderTop: `1px solid ${bdr}` }}>
                            <button className="w-full text-center font-medium text-sm"
                              style={{ color: isMovedOut ? '#6B7280' : P }}>
                              {isMovedOut ? 'View Record →' : 'View Details →'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                ) : (
                  /* ── Table ── */
                  <div className="rounded-xl overflow-hidden" style={card}>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead style={{ backgroundColor: bg.tertiary, borderBottom: `1px solid ${bdr}` }}>
                          <tr>
                            {['Tenant', 'Contact', 'Property', 'Unit', 'Rent', 'Balance', 'Status', 'Actions'].map(col => (
                              <th key={col} className="px-6 py-4 text-left text-xs font-bold uppercase"
                                style={{ color: txt.secondary }}>{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredTenants.map(tenant => {
                            const f = formatTenantForDisplay(tenant);
                            const isMovedOut = f.status === 'Moved Out';
                            return (
                              <tr key={tenant.id}
                                style={{ borderBottom: `1px solid ${bdr}`, opacity: isMovedOut ? 0.7 : 1 }}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = bg.tertiary}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>

                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                                      style={{ backgroundColor: isMovedOut ? '#9CA3AF' : f.balance < 0 ? Colors.status.error : Colors.status.success }}>
                                      {f.initials}
                                    </div>
                                    <div>
                                      <div className="font-semibold flex items-center gap-2" style={{ color: txt.primary }}>
                                        {f.name}
                                        {isMovedOut && (
                                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium"
                                            style={{ backgroundColor: 'rgba(107,114,128,0.15)', color: '#6B7280' }}>
                                            <LogOut className="w-2.5 h-2.5" /> Moved Out
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-xs" style={{ color: txt.muted }}>ID: {f.accountId}</div>
                                    </div>
                                  </div>
                                </td>

                                <td className="px-6 py-4">
                                  <div className="text-sm" style={{ color: txt.primary }}>{f.email}</div>
                                  <div className="text-xs" style={{ color: txt.muted }}>{f.phone}</div>
                                </td>

                                <td className="px-6 py-4 text-sm" style={{ color: txt.primary }}>{f.property}</td>

                                <td className="px-6 py-4">
                                  <div className="font-semibold text-sm" style={{ color: txt.primary }}>{f.unit}</div>
                                  <div className="text-xs" style={{ color: txt.muted }}>{f.category}</div>
                                </td>

                                <td className="px-6 py-4 text-sm font-medium" style={{ color: txt.primary }}>
                                  KES {f.rent.toLocaleString()}
                                </td>

                                <td className="px-6 py-4">
                                  <span className="text-sm font-semibold"
                                    style={{ color: f.balance < 0 ? Colors.status.error : f.balance === 0 ? txt.secondary : Colors.status.success }}>
                                    KES {Number(f.balance).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                </td>

                                <td className="px-6 py-4">
                                  <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full"
                                    style={getStatusStyle(f.status)}>{f.status}</span>
                                </td>

                                {/* Actions — view only, no delete button */}
                                <td className="px-6 py-4 text-center">
                                  <button onClick={() => handleSelectTenant(tenant)}
                                    className="p-2 rounded-lg transition-colors" title="View"
                                    style={{ color: isMovedOut ? '#6B7280' : P }}>
                                    <Eye className="w-5 h-5" />
                                  </button>
                                </td>

                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* ── Add Tenant Modal ─────────────────────────────────────────────────
          No wrapper div needed — TenantForm is a MUI Dialog and manages
          its own overlay/positioning. Just render it directly.
      ──────────────────────────────────────────────────────────────────────── */}
      <TenantForm
        open={showAddTenantForm}
        onClose={() => setShowAddTenantForm(false)}
        onTenantAdded={handleTenantAdded}
      />

    </div>
  );
};

export default TenantsManagement;